import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { BackendVersionBadge } from "@/src/components/navbar/backend-version-badge";
import { Link, useLocation, useNavigate } from "react-router";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Logo } from "@/components/logo";
import { Icon } from "@/components/icons";
import { NotificationsPopover } from "@/components/notifications-popover";
import { logout as authLogout } from "@/src/services/auth_service";
import { captureEvent, resetUser } from "@/src/lib/posthog";
import { NAV_ACCESS, isAccountTypeAllowed } from "@/src/lib/nav-access";

type Tool = "screening" | "monitoring";

type NavKey = "dashboard" | "screening" | "batch" | "monitoring" | "settings" | "help";

type NavItem = {
  key: NavKey;
  label: string;
  href: string;
  renderIcon: (active: boolean) => ReactNode;
  requires?: Tool;
  external?: boolean;
  badgeCount?: number;
  matchPaths?: string[];
};

const SETTINGS_PATHS = ["/account-management", "/company-settings", "/api-settings"];

type SidebarProps = {
  tools?: Tool[];
  accountType?: string;
  userName?: string;
  userEmail?: string;
  backendVersion?: string;
  adminUrl?: string;
  onNavigate?: () => void;
  compact?: boolean;
  onToggleCompact?: () => void;
  collapsible?: boolean;
  notificationsOpen?: boolean;
  onToggleNotifications?: () => void;
  notificationsButtonRef?: RefObject<HTMLButtonElement | null>;
  notificationsUnreadCount?: number;
  specificLogo?: string;
};

const PRIMARY_NAV: NavItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    href: "/dashboard",
    renderIcon: (a) => <Icon name="dashboard" filled={a} />,
  },
  {
    key: "screening",
    label: "Single Screening",
    href: "/screening-portal",
    renderIcon: (a) => <Icon name="search" filled={a} />,
    requires: "screening",
  },
  {
    key: "batch",
    label: "Batch Screening",
    href: "/batch",
    renderIcon: (a) => <Icon name="upload_file" filled={a} />,
    requires: "screening",
  },
  {
    key: "monitoring",
    label: "Monitoring",
    href: "/monitoring-portal",
    renderIcon: (a) => <Icon name="visibility" filled={a} />,
    requires: "monitoring",
  },
];

const BOTTOM_NAV: NavItem[] = [
  {
    key: "settings",
    label: "Settings",
    href: "/account-management",
    renderIcon: (a) => <Icon name="settings" filled={a} />,
    matchPaths: SETTINGS_PATHS,
  },
  {
    key: "help",
    label: "Help Center",
    href: "https://help.sanctions.io/knowledge-base",
    renderIcon: (a) => <Icon name="indeterminate_question_box" filled={a} />,
    external: true,
  },
];

export function Sidebar({
  tools = ["screening", "monitoring"],
  accountType,
  userName: userNameProp,
  userEmail: userEmailProp,
  backendVersion,
  adminUrl,
  onNavigate,
  compact: compactProp,
  onToggleCompact,
  collapsible = true,
  notificationsOpen: notificationsOpenProp,
  onToggleNotifications,
  notificationsButtonRef,
  notificationsUnreadCount = 0,
  specificLogo,
}: SidebarProps) {
  const [internalCompact, setInternalCompact] = useState(false);
  const compact = compactProp ?? internalCompact;
  const toggleCompact = onToggleCompact ?? (() => setInternalCompact((v) => !v));
  const notificationsControlled = onToggleNotifications !== undefined;
  const [internalNotificationsOpen, setInternalNotificationsOpen] = useState(false);
  const internalNotificationsButtonRef = useRef<HTMLButtonElement>(null);
  const notificationsOpen = notificationsControlled
    ? !!notificationsOpenProp
    : internalNotificationsOpen;
  const toggleNotifications = notificationsControlled
    ? onToggleNotifications!
    : () => setInternalNotificationsOpen((v) => !v);
  const notificationsButton = notificationsButtonRef ?? internalNotificationsButtonRef;
  const pathname = useLocation().pathname;

  const userName = userNameProp ?? "";
  const userEmail = userEmailProp ?? "";

  const handleNavigate = () => {
    if (!notificationsControlled) setInternalNotificationsOpen(false);
    onNavigate?.();
  };

  const allowedByRole = (key: NavKey) => {
    const rule = NAV_ACCESS[key as keyof typeof NAV_ACCESS];
    return !rule || isAccountTypeAllowed(accountType, rule);
  };

  const visiblePrimary = PRIMARY_NAV.filter(
    (item) => (!item.requires || tools.includes(item.requires)) && allowedByRole(item.key),
  );
  const visibleBottom = BOTTOM_NAV.filter((item) => allowedByRole(item.key));

  const logoWhiteFilter: React.CSSProperties | undefined = specificLogo?.includes("waco")
    ? { filter: "brightness(0) invert(1)" }
    : undefined;

  return (
    <aside
      className={[
        "sticky top-0 flex h-screen shrink-0 flex-col gap-2.5 bg-black px-1.5 py-3 transition-[width] duration-150",
        compact ? "w-13" : "w-55",
      ].join(" ")}
    >
      <header
        className={["flex p-1.5", compact ? "flex-col items-center gap-2.5" : "items-center"].join(
          " ",
        )}
      >
        {compact ? (
          <img
            src={specificLogo ?? "/brand/logo-collapsed.svg"}
            alt="sanctions.io"
            className="h-5 w-auto"
            style={logoWhiteFilter}
          />
        ) : (
          <div className="flex flex-1 justify-center">
            <Logo
              variant="white"
              overrideSrc={specificLogo}
              className="h-4 w-auto"
              style={logoWhiteFilter}
            />
          </div>
        )}
        {collapsible && (
          <button
            type="button"
            onClick={toggleCompact}
            className={[
              "flex size-8 items-center justify-center rounded-sm border border-grey-950 text-grey-300 transition-colors hover:text-white",
              compact ? "w-full" : "",
            ].join(" ")}
            aria-label={compact ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!compact}
          >
            <Icon name="keyboard_arrow_left" className={compact ? "rotate-180" : ""} />
          </button>
        )}
      </header>

      <Divider />

      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto" aria-label="Primary">
        {visiblePrimary.map((item) => (
          <NavLink
            key={item.key}
            item={item}
            active={isNavActive(pathname, item)}
            compact={compact}
            onNavigate={handleNavigate}
          />
        ))}
      </nav>

      <nav className="flex flex-col gap-1" aria-label="Secondary">
        <NotificationsNavButton
          open={notificationsOpen}
          onToggle={toggleNotifications}
          unreadCount={notificationsUnreadCount}
          compact={compact}
          buttonRef={notificationsButton}
        />
        {visibleBottom.map((item) => (
          <NavLink
            key={item.key}
            item={item}
            active={isNavActive(pathname, item)}
            compact={compact}
            onNavigate={handleNavigate}
          />
        ))}
      </nav>

      <Divider />

      {backendVersion && adminUrl && (
        <BackendVersionBadge
          backendVersion={backendVersion}
          adminUrl={adminUrl}
          compact={compact}
        />
      )}

      <UserRow name={userName} email={userEmail} compact={compact} />

      {!notificationsControlled && (
        <NotificationsPopover
          open={internalNotificationsOpen}
          onClose={() => setInternalNotificationsOpen(false)}
          anchorEl={internalNotificationsButtonRef.current}
          isMobile={false}
        />
      )}
    </aside>
  );
}

function isActive(pathname: string | null, href: string) {
  if (!pathname) return false;
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(href + "/");
}

function isNavActive(pathname: string | null, item: NavItem) {
  if (isActive(pathname, item.href)) return true;
  return item.matchPaths?.some((p) => isActive(pathname, p)) ?? false;
}

function NavLink({
  item,
  active,
  compact,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  compact: boolean;
  onNavigate?: () => void;
}) {
  const classes = [
    "flex items-center rounded-[4px] border py-2 pl-[3px] pr-2.5 transition-colors",
    compact ? "" : "gap-1.5",
    active
      ? "border-green-500-12 bg-green-500-12 text-white"
      : "border-transparent text-grey-300 hover:bg-grey-950",
  ].join(" ");

  const inner = (
    <>
      <span className="flex shrink-0 items-center gap-1">
        <span
          aria-hidden
          className={[
            "h-5 w-0.75 rounded-full",
            active ? "bg-green-300 shadow-glow" : "bg-transparent",
          ].join(" ")}
        />
        <span
          className={[
            "flex size-5 items-center justify-center",
            active ? "text-green-400" : "",
          ].join(" ")}
        >
          {item.renderIcon(active)}
        </span>
      </span>
      {!compact && (
        <>
          <span className="flex-1 text-small font-medium">{item.label}</span>
          {item.badgeCount !== undefined && item.badgeCount > 0 && (
            <span className="flex h-4 shrink-0 items-center justify-center rounded-sm bg-red-600 px-1 text-tiny font-medium text-white ring-2 ring-red-500/16">
              {item.badgeCount}
            </span>
          )}
          {item.external && (
            <Icon
              name="open_in_new"
              size={20}
              className="shrink-0 text-grey-500"
              label="Opens in a new tab"
            />
          )}
        </>
      )}
    </>
  );

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        title={compact ? item.label : undefined}
        className={classes}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      title={compact ? item.label : undefined}
      className={classes}
    >
      {inner}
    </Link>
  );
}

function NotificationsNavButton({
  open,
  onToggle,
  unreadCount,
  compact,
  buttonRef,
}: {
  open: boolean;
  onToggle: () => void;
  unreadCount: number;
  compact: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
}) {
  const active = open;
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-haspopup="dialog"
      title={compact ? "Notifications" : undefined}
      className={[
        "flex items-center rounded-sm border py-2 pl-0.75 pr-2.5 transition-colors",
        compact ? "" : "gap-1.5",
        active
          ? "border-green-500-12 bg-green-500-12 text-white"
          : "border-transparent text-grey-300 hover:bg-grey-950",
      ].join(" ")}
    >
      <span className="flex shrink-0 items-center gap-1">
        <span
          aria-hidden
          className={[
            "h-5 w-0.75 rounded-full",
            active ? "bg-green-300 shadow-glow" : "bg-transparent",
          ].join(" ")}
        />
        <span
          className={[
            "flex size-5 items-center justify-center",
            active ? "text-green-400" : "",
          ].join(" ")}
        >
          <Icon name={unreadCount > 0 ? "notifications_unread" : "notifications"} filled={active} />
        </span>
      </span>
      {!compact && <span className="flex-1 text-left text-small font-medium">Notifications</span>}
      {!compact && unreadCount > 0 && (
        <span className="flex h-4 shrink-0 items-center justify-center rounded-sm bg-red-600 px-1 text-tiny font-medium text-white ring-2 ring-red-500/16">
          {unreadCount}
        </span>
      )}
    </button>
  );
}

function UserRow({ name, email, compact }: { name: string; email: string; compact: boolean }) {
  const initial = ((email.trim() || name.trim()).charAt(0) || "?").toUpperCase();
  const [open, setOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handlePointer(e: MouseEvent) {
      const node = containerRef.current;
      if (node && !node.contains(e.target as Node)) setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
        className={[
          "flex w-full items-center rounded-sm p-1 text-left transition-colors hover:bg-grey-950",
          compact ? "justify-center" : "gap-2",
          open ? "bg-grey-950" : "",
        ].join(" ")}
      >
        <span
          className="flex size-7 shrink-0 items-center justify-center rounded-sm border border-green-500-12 bg-green-500-12 text-body font-semibold text-green-300"
          aria-hidden
        >
          {initial}
        </span>
        {!compact && (
          <>
            <span className="flex-1 truncate text-small font-medium text-white">{email}</span>
            <Icon
              name="keyboard_arrow_up"
              className={["text-grey-300 transition-transform", open ? "rotate-180" : ""].join(" ")}
            />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={[
            "absolute bottom-full z-50 mb-1 flex flex-col rounded-sm border-t border-grey-500-32 bg-grey-950 p-1 shadow-soft-black",
            compact ? "left-0 w-55" : "left-0 right-0",
          ].join(" ")}
        >
          <div className="flex flex-col px-2 py-1.5">
            <p className="text-tiny text-grey-500">Signed in as</p>
            <p className="truncate text-small font-medium text-white">{email}</p>
          </div>
          <div className="my-1 h-px bg-grey-900" aria-hidden />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setLogoutOpen(true);
            }}
            className="flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-small font-medium text-red-600 transition-colors hover:bg-grey-900"
          >
            <Icon name="logout" />
            <span>Log out</span>
          </button>
        </div>
      )}

      <ConfirmDialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => {
          setLogoutOpen(false);
          void (async () => {
            await authLogout();
            captureEvent("Logged out");
            resetUser();
            navigate("/login", { replace: true });
          })();
        }}
        title="Log out?"
        description="You'll be signed out and returned to the login page."
        confirmLabel="Log out"
        destructive
      />
    </div>
  );
}

function Divider() {
  return <div className="h-px w-full bg-grey-950" aria-hidden />;
}
